const MAIN_DOMAIN   = "nuvrahq.com";
const APP_SUBDOMAIN = `app.${MAIN_DOMAIN}`;

/**
 * Returns the vanity slug when the app is accessed via a subdomain URL.
 *
 * Handled patterns:
 *   app.nuvrahq.com/[slug]      → slug from first path segment
 *   [slug].nuvrahq.com          → slug from hostname (wildcard DNS, future)
 *
 * Returns null on the main domain, www subdomain, or localhost so that
 * normal authenticated routing is never interrupted in development.
 */
export function getSubdomainSlug(): string | null {
  const host = window.location.hostname;

  if (host === "localhost" || host.startsWith("127.") || host.startsWith("0.")) {
    return null;
  }

  // app.nuvrahq.com/[slug] - slug lives in the first path segment
  if (host === APP_SUBDOMAIN) {
    const first = window.location.pathname.split("/").filter(Boolean)[0];
    // Platform-owned public routes must reach the main router, not the vanity-app resolver.
    // In particular, Google Play requires /delete-account to be publicly reachable without
    // being interpreted as a customer app slug.
    const platformRoutes = new Set([
      "delete-account",
      "privacy",
      "terms",
      "acceptable-use",
      "ai-content",
      "security-policy",
      "cookie-policy",
      "pricing",
      "login",
      "signup",
      "forgot-password",
      "auth",
      "reset-password",
      "diagnostics",
    ]);
    if (!first || platformRoutes.has(first)) return null;
    return first;
  }

  // [slug].nuvrahq.com - slug lives in the hostname (future wildcard DNS)
  if (
    host !== MAIN_DOMAIN &&
    host !== `www.${MAIN_DOMAIN}` &&
    host.endsWith(`.${MAIN_DOMAIN}`)
  ) {
    return host.slice(0, -(`.${MAIN_DOMAIN}`.length));
  }

  return null;
}
